import { Button, Empty, Skeleton, Space, Tag, Tooltip, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import type { NocAlert, NocAlertSeverity } from '@goelectric/shared-types';
import { AlertSeverityBadge, AlertStatusBadge, AlertTypeBadge } from './StatusBadge';
import { formatRelative } from '../utils/format';

const SEVERITY_RANK: Record<NocAlertSeverity, number> = {
  CRITICAL: 0,
  WARNING: 1,
  INFO: 2,
};

interface AlertsPanelProps {
  alerts: NocAlert[];
  loading?: boolean;
  /** Id de la alerta que se está acusando (para el loading del botón). */
  ackingId?: string | null;
  onAcknowledge: (alertId: string) => void;
}

/** Panel de alertas priorizadas (abiertas primero) con acuse contra el backend. */
export function AlertsPanel({ alerts, loading = false, ackingId = null, onAcknowledge }: AlertsPanelProps) {
  if (loading && alerts.length === 0) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  if (alerts.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin alertas registradas" />;
  }

  const sorted = [...alerts].sort((a, b) => {
    const openDelta = Number(b.status === 'OPEN') - Number(a.status === 'OPEN');
    if (openDelta !== 0) {
      return openDelta;
    }
    const severityDelta = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (severityDelta !== 0) {
      return severityDelta;
    }
    return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
  });

  return (
    <ul className="noc-alerts">
      {sorted.map((alert) => (
        <li key={alert.id} className="noc-alerts__item">
          <div className="noc-alerts__head">
            <Space size={4} wrap>
              <AlertSeverityBadge severity={alert.severity} />
              <AlertTypeBadge type={alert.type} />
              <AlertStatusBadge status={alert.status} />
            </Space>
            {alert.status === 'OPEN' ? (
              <Tooltip title="Reconocer alerta (POST /alerts/:id/ack)">
                {/* Relleno (no `ghost`): el texto del primario ghost usa `colorPrimary`
                    remapeado por antd (#126a99, 2.97:1 sobre oscuro); el relleno da blanco
                    sobre el primario (≥ 4.5:1). */}
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={ackingId === alert.id}
                  onClick={() => onAcknowledge(alert.id)}
                >
                  Acusar
                </Button>
              </Tooltip>
            ) : (
              <Tag color={alert.status === 'ACKED' ? 'blue' : 'green'}>
                {alert.status === 'ACKED' ? 'Acusada' : 'Resuelta'}
              </Tag>
            )}
          </div>
          <Typography.Text strong className="noc-alerts__message">
            {alert.message}
          </Typography.Text>
          <Typography.Text type="secondary" className="noc-alerts__meta">
            {alert.stationId}
            {alert.connectorId !== null ? ` · conector ${alert.connectorId}` : ''} ·{' '}
            {formatRelative(alert.createdAt)}
          </Typography.Text>
        </li>
      ))}
    </ul>
  );
}
